import type { FC } from "react";
import { useRef, useEffect, memo } from "react";
import { Box, useMantineTheme } from "@mantine/core";
import { init } from "../utils/echarts";
import type { BarSeriesOption } from "echarts";
import { useIntl } from "react-intl";

export type Props = {
  series: BarSeriesOption | BarSeriesOption[];
  xLabel?: string;
  yLabel?: string;
  isUSD?: boolean;
  showLegend?: boolean;
};

export const EChartTimeseries: FC<Props> = memo(({
  series,
  xLabel,
  yLabel,
  isUSD = false,
  showLegend = false,
}) => {
  const intl = useIntl();
  const { colors: { gray } } = useMantineTheme();
  const chartRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const chartElement = chartRef.current;
    if (!chartElement) {
      return;
    }

    const chartInstance = init(chartElement);
    const resizeObserver = new ResizeObserver(() => {
      const changedWidth = chartInstance.getWidth() !== chartElement.clientWidth;
      const changedHeight = chartInstance.getHeight() !== chartElement.clientHeight;
      const changed = changedWidth || changedHeight;

      if (changed) {
        chartInstance.resize({
          silent: true,
        });
      }
    });

    resizeObserver.observe(chartElement);

    const splitLine = {
      show: true,
      lineStyle: {
        type: "dashed",
        color: gray[2],
      },
    };

    const option = {
      xAxis: {
        type: "time",
        name: xLabel,
        splitLine,
        axisLabel: {
          hideOverlap: true,
          formatter: (value: number | string) => {
            const timestamp = typeof value === "number" ? value : Number(value);
            const date = new Date(timestamp);

            const isMidnight =
              date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;

            if (isMidnight) {
              return intl.formatDate(date, { month: "short", day: "numeric" });
            }
            else {
              return intl.formatTime(date, { hour: "2-digit", minute: "2-digit" });
            }
          },
        },
      },
      yAxis: {
        type: "value",
        name: yLabel,
        splitLine,
        axisLabel: {
          formatter: (value: number) =>
            intl.formatNumber(value, {
              notation: "compact",
              style: isUSD ? "currency" : undefined,
              currency: isUSD ? "USD" : undefined,
            }),
        },
      },
      series,
      tooltip: {
        trigger: "axis",
      },
      legend: {
        show: showLegend,
        orient: "horizontal",
        bottom: 20,
        icon: "rect",
      },
      grid: {
        bottom: showLegend ? 100 : 0,
      },
    };

    chartInstance.setOption(option);

    return () => {
      resizeObserver.disconnect();
      chartInstance.dispose();
    };
  }, [series, xLabel, yLabel, showLegend, isUSD]);

  return <Box ref={chartRef} w="100%" h="100%" />;
});

EChartTimeseries.displayName = "EChartTimeseries";
